import { FC, useRef, useState } from 'react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Heading } from '@/components/typography';
import { useTranslation } from 'react-i18next';
import { NextArrow, PrevArrow } from '@/components/buttons/SliderButtons';
import SliderComponent from 'react-slick';
import { useSliderParamsGenerator } from '@/hooks';
import DecorIcon from '@/public/icons/team/recruiting-stages-slider.svg';

interface IStages {
  id: number;
  description: string;
}

interface SyncSlider {
  stages: IStages[];
  className?: string;
  sliderToShow?: number;
}

export const RecruitingStagesSlider: FC<SyncSlider> = ({ stages }) => {
  const { t } = useTranslation('common');
  const title = t('recruitingStagesTitle');
  const sliderRef = useRef<SliderComponent>(null);
  const slidesToShow = useSliderParamsGenerator(
    stages.length,
    512,
    2.05,
    3.4,
    1.4,
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const slidesToScroll = 1;
  const last = stages.length - 1;

  const handlePrevClick = () => {
    if (isAnimating) return;
    if (activeIndex !== 0) {
      sliderRef.current?.slickPrev();
      setActiveIndex(prev => prev - slidesToScroll);
    }
  };

  const handleNextClick = () => {
    if (isAnimating) return;
    if (activeIndex !== last) {
      sliderRef.current?.slickNext();
      setActiveIndex(prev => prev + slidesToScroll);
    }
  };

  const handleOnSwipe = (swipeDirection: 'left' | 'right') => {
    if (isAnimating) return;
    if (swipeDirection === 'left' && activeIndex !== last) {
      sliderRef.current?.slickNext();
      setActiveIndex(prev => prev + slidesToScroll);
    }
    if (swipeDirection === 'right' && activeIndex !== 0) {
      sliderRef.current?.slickPrev();
    }
  };

  const handleBeforeChange = () => setIsAnimating(true);
  const handleAfterChange = () => setIsAnimating(false);
  const isPrevDisabled = activeIndex === 0;
  const isNextDisabled = activeIndex >= last;

  const sliderSettings = {
    prevArrow: (
      <PrevArrow onClick={handlePrevClick} disabled={isPrevDisabled} />
    ),
    nextArrow: (
      <NextArrow onClick={handleNextClick} disabled={isNextDisabled} />
    ),
  };

  const implementedSettings = {
    speed: 700,
    slidesToScroll,
    autoplay: false,
    arrows: false,
    infinite: false,
    slidesToShow,
  };

  return (
    <div>
      <div className="recruitingStages__title-wrapper align-center mb-4 flex justify-between md:mb-[48px]">
        <Heading tag="h2" className="recruitingStages__title" content={title} />
        <div className="custom-slider-arrows mb-8 hidden justify-end gap-7 md:mb-0 md:flex">
          {sliderSettings.prevArrow}
          {sliderSettings.nextArrow}
        </div>
      </div>
      <div className="recruitingStages__slider">
        <div className="custom-slider-arrows mb-8 flex justify-end gap-7 md:hidden">
          {sliderSettings.prevArrow}
          {sliderSettings.nextArrow}
        </div>
        <SliderComponent
          ref={sliderRef}
          {...implementedSettings}
          onSwipe={handleOnSwipe}
          beforeChange={handleBeforeChange}
          afterChange={handleAfterChange}
        >
          {stages.map((item, index) => {
            const isPassed = index < activeIndex;
            const isActive = index === activeIndex;
            return (
              <div
                key={index}
                className={`recruitingStages__slider-item ${
                  isPassed ? 'isPassed' : ''
                } ${isActive ? 'isActive' : ''} num${index}`}
              >
                <div className="recruitingStages__slider-item-num  mb-6">{`0${
                  index + 1
                }`}</div>
                <div className="recruitingStages__slider-item-description">
                  {item.description}
                </div>
                {isActive && (
                  <div className="recruitingStages__slider-icon-wrapper">
                    <DecorIcon className="recruitingStages__slider-item-icon" />
                    <DecorIcon className="recruitingStages__slider-item-icon" />{' '}
                    <DecorIcon className="recruitingStages__slider-item-icon" />
                  </div>
                )}
              </div>
            );
          })}
        </SliderComponent>
      </div>
    </div>
  );
};
